import React from 'react'
import lodash from 'lodash'

import ConfiguratorAppContext from '../ConfiguratorAppContext'

import './ListScreen.scss'
import { inflateListItems } from '../products/productUtils'
import { ProductDataContext } from '../ProductDataContext'
import useCloudSharedList, { ListDoesntExistError } from '../hooks/useCloudSharedList'
import LoadingScreen from '../components/LoadingScreen'
import SharedBomScreen from './SharedBomScreen'
import TabSelector from '../components/TabSelector'
import { ConfiguredProductListItem, InflatedListItemProduct, Room } from './ListTypes'
import ErrorBoundary from '../components/ErrorBoundary'
import DisclaimerBox from './DisclaimerBox'
import { RoomHeader, RoomSection } from './RoomSection'
import { generateFullBom, getBomProductsForInflatedItem, InflatedProductBomWithRoom } from '../bom/bomUtils'
import { IconDownload } from '../components/icons'
import Button from '../components/Button'
import { Cross } from '../characters'
import { BuildConfig } from '../config/BuildConfig'
import { ListItemDetails } from './ListItemDetails'
import { onDownloadBom, onDownloadList } from '../utils/downloadList'
import { ResponsiveBrandLogo } from '../components/ResponsiveBrandLogo'

import './SharedListScreen.scss'
import { SectionHeaderRow } from './SectionHeaderRow'
import { ProductPriceUnavailable } from '../productPricing/ProductPricingContext'
import { getListItemPricing, isBulkPackProduct } from '../productPricing/productPricingCalculation'
import { useProductPrices } from '../hooks/useProductPrices'
import { ListPrice } from './ListPrice'
import { getListInterfaceDisplayName } from './listItemUtils'
import { ComponentListItemRow } from '../components/ComponentListItemRow'
import { Spinner } from '../components/Spinner'
import { isProductApiAvailable } from '../productDataApi/productDataApi'
import { noOpPackQuantityProvider } from '../bom/NoOpPackQuantityProvider'
import { AnalyticsContext } from '../analytics/AnalyticsContext'
import { CookieSettingsLink } from '../components/CookieSettingsLink'
import { PrivacyPolicyLink } from '../components/PrivacyPolicyLink'
import { getAnalyticsProductInfo, getEcommercePayloadFrom } from '../analytics/AnalyticsHelpers'
import { usePageViewEvent } from '../analytics/usePageViewEvent'

interface Props {
  products: readonly Readonly<InflatedListItemProduct>[]
  rooms: readonly Room[]
  listName: string
  listId: string
  onClickDownloadList: (products: readonly Readonly<InflatedListItemProduct>[]) => void
}

const SharedListScreen: React.FunctionComponent<Props> = ({
  products,
  rooms,
  listName,
  listId,
  onClickDownloadList,
}) => {
  const analyticsLogger = React.useContext(AnalyticsContext)

  const productsByRoom = lodash.groupBy(products, it => it.roomId ?? '')

  // const { isAuthenticated } = React.useContext(ConfiguratorAppContext)

  const roomsToShow = [{
    name: '',
    uid: '',
  }].concat(rooms)

  const shouldShowPricing = isProductApiAvailable()

  const fullListBom = generateFullBom(products.map(it => getBomProductsForInflatedItem(it)), noOpPackQuantityProvider)
  const fullListBomItemNos = fullListBom.map(it => it.itemNo)

  const { priceMap, priceTypeLabel, isFetching: isFetchingPricing, isEnabled: isPricingEnabled } = useProductPrices(shouldShowPricing ? fullListBomItemNos : [])

  const bulkPacksToDisplay = shouldShowPricing ? fullListBom.filter(isBulkPackProduct) : []

  return (
    <div className="SharedListScreen">
      <DisclaimerBox />
      <div className="centerContainer">
        <SectionHeaderRow title={listName}>
          {
            isFetchingPricing && (
              <Spinner size={20} inline/>
            )
          }
          <Button
            disabled={isFetchingPricing}
            buttonStyle="green"
            onClick={() => {
              analyticsLogger.log({
                event: 'file_download',
                document_id: null,
                document_name: 'Iconic Export',
                document_type: 'csv'
              })

              const analyticsProducts = products.map(product => ({
                product: getAnalyticsProductInfo(product.skinProduct),
                quantity: product.quantity,
                price: getListItemPricing(generateFullBom([getBomProductsForInflatedItem(product)], noOpPackQuantityProvider), priceMap).price?.toString() ?? null
              }))

              analyticsLogger.log({
                event: 'add_to_cart',
                distributor: null,
                add_to_cart_location: 'Shared list',
                cta_name: 'Download BOM',
                ecommerce: getEcommercePayloadFrom(
                  analyticsProducts,
                  {
                    name: listName,
                    id: listId,
                  }
                )
              })

              onClickDownloadList(products)
            }}
          >
            <img src={IconDownload} alt="" style={{ marginRight: '10px' }} />
            {BuildConfig.brand.copy.ButtonDownload}
          </Button>
        </SectionHeaderRow>

        {
          roomsToShow.map(room => {
            const productsInRoom = productsByRoom[room.uid] ?? []
            const isUnroomedSection = room.uid === ''
            if (isUnroomedSection && productsInRoom.length === 0) {
              return null
            }

            return (
              <RoomSection key={room.uid}>
                {
                  !isUnroomedSection && (
                    <RoomHeader>
                      {room.name}
                    </RoomHeader>
                  )
                }

                {
                  productsInRoom.length === 0 && (
                    <div className="emptyRoom">
                      This room is empty.
                    </div>
                  )
                }

                {
                  productsInRoom.map((configuredProduct) => {
                    const listItemBom = generateFullBom([getBomProductsForInflatedItem(configuredProduct)], noOpPackQuantityProvider)

                    const pricingParams = shouldShowPricing ? getListItemPricing(listItemBom, priceMap) : null

                    return (
                      <div
                        key={configuredProduct.uid}
                        className="ListItemRow"
                      >
                        <div className="quantityBar">
                          <div className="details">
                            <ListItemDetails
                              configuredProduct={configuredProduct}
                              showPrice={false}
                              // isAuthenticated={isAuthenticated}
                              priceLoading={false}
                              price={ProductPriceUnavailable}
                              priceDisclaimer={null}
                            />
                          </div>

                          <div className="quantity">
                            {Cross} {configuredProduct.quantity}
                          </div>

                          <ListPrice
                            showPrice={shouldShowPricing}
                            priceLoading={pricingParams?.price === ProductPriceUnavailable && isFetchingPricing}
                            price={pricingParams?.price ?? ProductPriceUnavailable}
                            priceDisclaimer={pricingParams?.disclaimer ?? null}
                            priceTypeLabel={priceTypeLabel}
                          />
                        </div>
                        {
                          configuredProduct.note && (
                            <div className="note">
                              <strong>Notes:</strong> {configuredProduct.note}
                            </div>
                          )
                        }
                      </div>
                    )
                  })
                }
              </RoomSection>
            )
          })
        }

        {
          bulkPacksToDisplay.length > 0 && (
            <>
              <RoomHeader borderTop>
                Bulk Packs
              </RoomHeader>

              {
                bulkPacksToDisplay
                  .map(bomItem => {
                    const price = priceMap[bomItem.itemNo] ?? ProductPriceUnavailable

                    // show the display name for interfaces, or fall back to description for other products
                    const interfaceItem = null//Products.interfaceMap[bomItem.itemNo] ?? null
                    const displayName = interfaceItem ? getListInterfaceDisplayName(interfaceItem, null) : bomItem.description

                    return (
                      <ComponentListItemRow
                        key={bomItem.itemNo}
                        bomItem={bomItem}
                        displayName={displayName}
                        priceLoading={price === ProductPriceUnavailable && isFetchingPricing}
                        price={price ?? ProductPriceUnavailable}
                        priceTypeLabel={priceTypeLabel}
                        showPrice={isPricingEnabled}
                      />
                    )
                  })
              }
            </>
          )
        }

      </div>
    </div >
  )
}

export const SharedListPreviewScreen: React.FunctionComponent<{
  products: readonly Readonly<ConfiguredProductListItem>[]
  rooms: readonly Room[]
  listName: string
}> = ({ products, rooms }) => {
  const { listName, listId } = React.useContext(ConfiguratorAppContext)
  const Products = React.useContext(ProductDataContext)

  const configuredProducts: readonly InflatedListItemProduct[] = inflateListItems(products, Products)

  return (
    <SharedListScreen
      products={configuredProducts}
      rooms={rooms}
      listName={listName}
      listId={listId}
      onClickDownloadList={()=>{ return }}
    />
  )
}

export const SharedListLoaderScreen: React.FunctionComponent<{
  listId: string
  viewType?: string | string[] | null
}> = ({ listId, viewType }) => {
  React.useEffect(() => {
    document.title = `${BuildConfig.brand.builderTitle} List`
  }, [])

  usePageViewEvent({ userType: null, pageCategory: 'list' })

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const [loading, error, list] = useCloudSharedList(listId)
  // const [showDownloadBomPopup, setShowDownloadBomPopup] = React.useState(false)
  // const [showDownloadListPopup, setShowDownloadListPopup] = React.useState(false)
  // const [listProducts, setListProducts] = React.useState<readonly Readonly<InflatedListItemProduct>[]>([])

  const [selectedTab, setSelectedTab] = React.useState<'list' | 'bom'>(() => {
    if (viewType === 'list' || viewType === 'bom') {
      return viewType
    }

    return 'list'
  })

  const getBomProducts = (): readonly InflatedProductBomWithRoom[] => {
    return list?.configuredProducts.map(product => ({
      ...getBomProductsForInflatedItem(product),
      roomId: product.roomId,
    })) ?? []
  }

  const { priceMap, priceTypeLabel } = useProductPrices(generateFullBom(getBomProducts(), noOpPackQuantityProvider).map(it => it.itemNo))

  if (error || !list) {
    if (error === ListDoesntExistError) {
      return (
        <h3 style={{ textAlign: 'center' }}>
          A list doesn&apos;t exist here, check that the link is correct.
        </h3>
      )
    }

    return (
      <h3 style={{ textAlign: 'center' }}>
        Couldn&apos;t load list...
      </h3>
    )
  }

  return (
    <div className="SharedListPage">
      <div
        style={{
          maxWidth: '800px',
          margin: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          width: '100%',
          borderBottom: '1px solid #ddd',
          background: '#fff',
          height: '100%',
          justifyContent: 'flex-start',
        }}
      >
        <div className="sharedListHeader">
          <ResponsiveBrandLogo brand={BuildConfig.brand} />
          <div style={{ flex: '1 1 0' }} />
          <TabSelector
            selected={selectedTab}
            onClick={it => setSelectedTab(it)}
            options={[{
              key: 'list',
              label: 'List'
            }, {
              key: 'bom',
              label: 'Bill of Materials'
            }] as const}
          />
          <div style={{ flex: '1 1 0' }} />
        </div>
        <div
          style={{
            flex: '1 1 0',
            overflowY: 'scroll',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {
            loading && (
              <LoadingScreen/>
            )
          }
          {
            !loading && selectedTab === 'list' && (
              <ErrorBoundary
                onError={(
                  <div style={{ flex: '1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <h3>Something went wrong with this list..</h3>
                  </div>
                )}
              >
                <SharedListScreen
                  products={list.configuredProducts}
                  rooms={list.rooms}
                  listName={list.name}
                  listId={listId}
                  onClickDownloadList={(products) => {
                    onDownloadList(products, noOpPackQuantityProvider, { label: priceTypeLabel, priceMap })
                  }}
                />
              </ErrorBoundary>
            )
          }
          {
            !loading && selectedTab === 'bom' && (
              <ErrorBoundary
                onError={(
                  <div style={{ flex: '1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <h3>Something went wrong with this list..</h3>
                  </div>
                )}
              >
                <SharedBomScreen
                  products={getBomProducts()}
                  rooms={list.rooms}
                  scrollable={false}
                  onClickDownloadList={
                    () => {
                      onDownloadBom(getBomProducts(), noOpPackQuantityProvider, { label: priceTypeLabel, priceMap })
                    }
                  }
                />
              </ErrorBoundary>
            )
          }
          {/*{showDownloadBomPopup && (*/}
          {/*  <DownloadListPopup*/}
          {/*    onClose={() => setShowDownloadBomPopup(false)}*/}
          {/*    onSubmit={(value) => {*/}
          {/*      onDownloadBom(getBomProducts(), { label: priceTypeLabel, priceMap }, value)*/}
          {/*    }}*/}
          {/*  />*/}
          {/*)}*/}
          {/*{showDownloadListPopup && (*/}
          {/*  <DownloadListPopup*/}
          {/*    onClose={() => setShowDownloadListPopup(false)}*/}
          {/*    onSubmit={(value) => {*/}
          {/*      onDownloadList(listProducts, { label: priceTypeLabel, priceMap }, value)*/}
          {/*    }}*/}
          {/*  />*/}
          {/*)}*/}
          <div style={{ flex: '1 0 30px' }}/>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
            <PrivacyPolicyLink/>
            <CookieSettingsLink/>
          </div>
        </div>
      </div>
    </div>
  )
}
